import React, { useCallback, useMemo, useState } from 'react';
import type { CharacterPortrait, Scenario, Scene } from '../../types';
import { InlineContentLine } from './InlineContentLine';
import { InlineBackgroundPanel } from './InlineBackgroundPanel';
import {
  buildBackgroundSections,
  clearSceneBackground,
  computeHiddenSceneIds,
  hasLocalBackground,
  isSectionCollapsed,
  isBackgroundSectionMember,
  sectionForScene,
  setSceneBackground,
} from './inline-background-model';
import type { InlineScenarioChangeOptions } from './use-inline-block-history';
import type { OpenSceneInEditorHandler } from './inline-elements-model';
import type { SortCategoryActions } from './sort-category-actions';
import inline from './inline-editor.module.css';

export interface FlatBlock {
  blockId: string;
  item: Scene;
  sceneId: string;
  contentIndex: number;
  flatIndex: number;
}

interface InlineSceneListProps {
  scenes: Scene[];
  flatBlocks: FlatBlock[];
  scenarioId: string;
  scenario: Scenario;
  editMode: boolean;
  characters: CharacterPortrait[];
  sortCategoryActions?: SortCategoryActions;
  onChange: (flatIndex: number, item: Scene, options?: InlineScenarioChangeOptions) => void;
  onRemove: (flatIndex: number) => void;
  onMove: (flatIndex: number, dir: -1 | 1) => void;
  onOpenSceneInEditor?: OpenSceneInEditorHandler;
  onPreviewBlock?: (sceneId: string) => void;
  /** Affiche l'id de scène sur le premier bloc (début de chapitre). */
  annotateFirstBlockSceneId?: boolean;
}

export const InlineSceneList: React.FC<InlineSceneListProps> = ({
  scenes,
  flatBlocks,
  scenarioId,
  scenario,
  editMode,
  characters,
  sortCategoryActions,
  onChange,
  onRemove,
  onMove,
  onOpenSceneInEditor,
  onPreviewBlock,
  annotateFirstBlockSceneId = false,
}) => {
  const sections = useMemo(() => buildBackgroundSections(scenes), [scenes]);
  const [collapsedAnchors, setCollapsedAnchors] = useState<Set<string>>(() => new Set());

  const hiddenIds = useMemo(
    () => computeHiddenSceneIds(scenes, sections, collapsedAnchors),
    [scenes, sections, collapsedAnchors]
  );

  const toggleSection = useCallback((anchorSceneId: string) => {
    setCollapsedAnchors((prev) => {
      const next = new Set(prev);
      if (next.has(anchorSceneId)) next.delete(anchorSceneId);
      else next.add(anchorSceneId);
      return next;
    });
  }, []);

  return (
    <>
      {flatBlocks.map((block) => {
        const section = sectionForScene(sections, block.sceneId);
        const collapsed = section
          ? isSectionCollapsed(section.anchorSceneId, collapsedAnchors)
          : false;

        if (collapsed && section && block.sceneId === section.anchorSceneId) {
          if (!hasLocalBackground(block.item)) return null;
          return (
            <div key={block.blockId} className={inline.bgSectionCollapsed}>
              <InlineBackgroundPanel
                scenarioId={scenarioId}
                backgroundPath={block.item.background!}
                editMode={editMode}
                onPick={(path) =>
                  onChange(
                    block.flatIndex,
                    setSceneBackground(block.item, path),
                    { recordBlockHistory: true }
                  )
                }
                onRemove={() =>
                  onChange(
                    block.flatIndex,
                    clearSceneBackground(block.item),
                    { recordBlockHistory: true }
                  )
                }
                sectionToggle={{
                  sceneCount: section.sceneCount,
                  collapsed: true,
                  onToggle: () => toggleSection(section.anchorSceneId),
                }}
              />
            </div>
          );
        }

        if (hiddenIds.has(block.sceneId)) return null;

        const sectionToggle = section
          ? {
              sceneCount: section.sceneCount,
              collapsed: false,
              onToggle: () => toggleSection(section.anchorSceneId),
            }
          : undefined;

        const sectionMember = isBackgroundSectionMember(sections, block.sceneId, scenes);

        return (
          <InlineContentLine
            key={block.blockId}
            blockId={block.blockId}
            item={block.item}
            flatIndex={block.flatIndex}
            sceneId={block.sceneId}
            contentIndex={block.contentIndex}
            editMode={editMode}
            characters={characters}
            sortCategoryActions={sortCategoryActions}
            flatBlocks={flatBlocks}
            scenarioId={scenarioId}
            scenario={scenario}
            inBackgroundSection={sectionMember}
            backgroundSectionToggle={sectionToggle}
            onChange={onChange}
            onRemove={onRemove}
            onMove={onMove}
            onOpenSceneInEditor={onOpenSceneInEditor}
            onPreviewBlock={onPreviewBlock}
            isFirstInChapter={annotateFirstBlockSceneId && block.flatIndex === 0}
          />
        );
      })}
    </>
  );
};
